import { useCallback, useEffect } from "react";

import type { HostedProject } from "./api";
import {
  hostedApiErrorCode,
  hostedApiErrorMessage,
  useHostedApiClient,
} from "./useHostedApiClient";
import { recordHostedAccessResult } from "./hostedAccessStore";
import { loadHostedProjects, useHostedProjectStore } from "./hostedProjectStore";

/**
 * Load and create hosted projects. Every call funnels its authorization outcome
 * into the access store so the owner gate reflects the Worker's answer without a
 * second probe request.
 *
 * Ordering and de-duplication belong to the store, not to this hook: several
 * surfaces mount this at once, so a ref here could only ever see its own
 * instance's request. `loadHostedProjects` shares one in-flight list between all
 * of them and stamps the answer with a token the store can compare.
 */
export function useHostedProjects(options?: { readonly enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const client = useHostedApiClient();
  const upsertAndSelectProject = useHostedProjectStore((state) => state.upsertAndSelectProject);
  const selectProject = useHostedProjectStore((state) => state.selectProject);
  const projects = useHostedProjectStore((state) => state.projects);
  const selectedProjectId = useHostedProjectStore((state) => state.selectedProjectId);
  const status = useHostedProjectStore((state) => state.status);
  const errorMessage = useHostedProjectStore((state) => state.errorMessage);
  const accountEpoch = useHostedProjectStore((state) => state.accountEpoch);

  const refresh = useCallback(async () => {
    const outcome = await loadHostedProjects({
      list: () => client.listProjects(),
      describeError: hostedApiErrorMessage,
    });
    recordHostedAccessResult(
      outcome.ok
        ? { code: null }
        : {
            code: hostedApiErrorCode(outcome.error),
            message: hostedApiErrorMessage(outcome.error),
          },
    );
  }, [client]);

  const createProject = useCallback(
    async (name: string): Promise<HostedProject> => {
      try {
        const created = await client.createProject({ name });
        recordHostedAccessResult({ code: null });
        upsertAndSelectProject(created);
        return created;
      } catch (error) {
        recordHostedAccessResult({
          code: hostedApiErrorCode(error),
          message: hostedApiErrorMessage(error),
        });
        throw error;
      }
    },
    [client, upsertAndSelectProject],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void refresh();
    // `accountEpoch` changes when the store is cleared for a new account. Without
    // it a surface that is already mounted would keep showing the emptied list,
    // because nothing else re-runs this effect after a switch.
  }, [accountEpoch, enabled, refresh]);

  return {
    projects,
    selectedProjectId,
    status,
    errorMessage,
    refresh,
    createProject,
    selectProject,
  };
}
